import React, { useState, useEffect } from "react";
import { Link, useNavigate } from "react-router-dom";
import { app } from "../firebase"; // Ensure you have this Firebase configuration file

import {
  getAuth,
  createUserWithEmailAndPassword,
  updateProfile,
} from "firebase/auth";
import MyLottieAnimation from "../components/LottieAnimation";
import { FirebaseError } from "firebase/app";
import { trackClick } from "../utils/analytics";
import { attachReferral } from "../utils/referral";
import { requestVerificationEmail, rememberVerificationSent } from "../utils/emailVerification";
import LanguageSelector from "../components/LanguageSelector";
import { useTranslation } from "react-i18next";

const SignUpPage: React.FC = () => {
  const { t } = useTranslation();
  const [formData, setFormData] = useState({
    name: "",
    email: "",
    password: "",
  });
  const [userCreated, setUserCreated] = useState(false);
  const [emailError, setEmailError] = useState("");
  const [passwordError, setPasswordError] = useState("");
  const navigate = useNavigate();
  var arg = new URLSearchParams(window.location.search);

  useEffect(() => {
    if (userCreated) {
      navigate("/onboarding");
    }
  }, [userCreated, navigate]);

  const handleChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    setFormData({ ...formData, [e.target.name]: e.target.value });

    // Validate email
    if (e.target.name === "email") {
      setEmailError(validateEmail(e.target.value));
    }

    // Validate password
    if (e.target.name === "password") {
      setPasswordError(validatePassword(e.target.value));
    }
  };

  const handleSubmit = async (e: React.FormEvent<HTMLFormElement>) => {
    var hasSubmitted = false
    if (hasSubmitted) {
      return;
    }
    hasSubmitted = true
    localStorage.setItem("user_id", formData.email);
    trackClick("button_click-sign_up");
    e.preventDefault();

    // Password validation logic
    if (validatePassword(formData.password) !== "") {
      console.error(
        "Invalid password. Please ensure your password meets the requirements."
      );
      hasSubmitted = false
      return;
    }

    const auth = getAuth(app);
    try {
      const userCredential = await createUserWithEmailAndPassword(
        auth,
        formData.email,
        formData.password
      );
      await updateProfile(userCredential.user, {
        displayName: formData.name ?? "",
      });
      console.log("User created:", userCredential.user);
      await attachReferral(userCredential.user);
      // New accounts get the confirmation email straight away; the portal stays behind the verify screen until it is confirmed.
      if ((await requestVerificationEmail(() => userCredential.user.getIdToken())).status === "sent") {
        rememberVerificationSent(userCredential.user.uid);
      }
      setUserCreated(true);
      trackClick("success-sign_up");
      await sendEmail()
    } catch (error) {
      if (error instanceof FirebaseError) {
        alert("Error creating user:" + error.message);
      }
      console.error("Error creating user:", error);
    }
    hasSubmitted = false;
  };

  async function sendEmail() {
    const subject = `${formData.email} ${formData.name} created an account`;
    const body = "Send an email because they created an account";
    const params = new URLSearchParams({
      subject,
      body,
    });
    await fetch(
      `https://api.deafassistant.com/email/sendemail?${params.toString()}`,
      {
        method: "POST",
      }
    );
  }

  const validateEmail = (email: string) => {
    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    if (!emailRegex.test(email)) {
      return t("auth.invalidEmail");
    }
    return "";
  };

  const validatePassword = (password: string) => {
    const minLength = 6;
    const hasUppercase = /[A-Z]/.test(password);
    const hasLowercase = /[a-z]/.test(password);
    const hasNumber = /\d/.test(password);
    const hasSpecialChar = /[!@#$%^&*(),.?":{}|<>]/.test(password);

    if (
      password.length < minLength ||
      !hasUppercase ||
      !hasLowercase ||
      !hasNumber ||
      !hasSpecialChar
    ) {
      return t("auth.passwordRequirements");
    }

    return "";
  };

  return (
    <div className="flex flex-col lg:flex-row ">
      {/* Language Selector - Top Right */}
      <div className="absolute top-4 right-4 z-10">
        <LanguageSelector />
      </div>

      {/* Left Column */}
      <div className="w-full lg:w-1/2 flex flex-col justify-start  items-center lg:items-start px-6 lg:px-12 py-8 lg:py-12 lg:pt-24">
        <a
          href={
            "https://" +
            (arg.get("s") === "khutbah"
              ? "khutbahcaptioning"
              : "conferencecaptioning") +
            ".com"
          }
        >
          <img
            src={
              "https://" +
              (arg.get("s") === "khutbah" || arg.get("s") === "transit"
                ? arg.get("s") + "captioning.com/images/logo-dark.svg"
                : "conferencecaptioning.com/images/logo.png")
            }
            alt="Logo"
            className="h-12 mb-6"
          />
        </a>

        <h1 className="text-2xl lg:text-3xl font-bold text-gray-800 mb-2">
          {t("auth.signupTitle")}
        </h1>
        <p className="mb-8">
          {t("auth.signupSubtitle")}{" "}
          <Link to="/login" className="text-blue-600 hover:underline">
            {t("auth.loginHere")}
          </Link>
        </p>

        <form onSubmit={handleSubmit} className="w-full max-w-md">
          <div className="flex-col justify-center ">
            <div className="mb-4">
              <input
                type="text"
                id="name"
                name="name"
                value={formData.name}
                onChange={handleChange}
                placeholder={t("auth.name")}
                className="w-[105%] bg-gray-100 rounded-2xl border border-gray-300 focus:ring-blue-500 focus:border-blue-500 text-base outline-none text-gray-700 py-1 px-3 leading-8 transition-colors duration-200 ease-in-out"
                required
              />
            </div>

            <div className="mb-4">
              <input
                type="email"
                id="email"
                name="email"
                value={formData.email}
                onChange={handleChange}
                placeholder={t("auth.email")}
                className="w-[105%] bg-gray-100 rounded-2xl border border-gray-300 focus:ring-blue-500 focus:border-blue-500 text-base outline-none text-gray-700 py-1 px-3 leading-8 transition-colors duration-200 ease-in-out"
                required
              />
              {emailError && (
                <p className="text-red-500 text-sm">{emailError}</p>
              )}
            </div>

            <div className="mb-6">
              <input
                type="password"
                id="password"
                name="password"
                value={formData.password}
                onChange={handleChange}
                placeholder={t("auth.password")}
                className="w-[105%] bg-gray-100 rounded-2xl border border-gray-300 focus:ring-blue-500 focus:border-blue-500 text-base outline-none text-gray-700 py-1 px-3 leading-8 transition-colors duration-200 ease-in-out"
                required
              />
              {passwordError && (
                <p className="text-red-500 text-sm">{passwordError}</p>
              )}
            </div>
            <div className="text-xs text-gray-500 mt-3 ">
              {t("auth.termsPrefix")}
              <a
                className="text-blue-600 hover:underline focus:outline-none"
                href="https://conferencecaptioning.com/terms-and-conditions.html"
              >
                {" "}
                {t("auth.termsAndConditions")}
              </a>{" "}
              {t("auth.and")}
              <a
                className="text-blue-600 hover:underline focus:outline-none"
                href="https://conferencecaptioning.com/privacy-policy.html"
              >
                {" "}
                {t("auth.privacyPolicy")}
              </a>{" "}
              {t("auth.termsSuffix")}
            </div>
            <div> </div>

            <div className="flex justify-center ml-3 mt-6">
              <button
                type="submit"
                className="text-white w-[100%] bg-blue-500 border-0 py-2 px-8 focus:outline-none hover:bg-blue-600 rounded-full text-lg leading-8"
              >
                {t("auth.letsGo")}
              </button>
            </div>
          </div>
        </form>

        <div className="flex justify-between  max-w-md mt-8">
          <hr className="w-1/4 border-t-2 border-gray-200" />

          <hr className="w-1/4 border-t-2 border-gray-200" />
        </div>

        {/* <div className="flex flex-col justify-between   max-w-xs mt-8 items-center" style={{ width: '25rem', maxWidth: '100%' }}>
          <button onClick={handleGoogleSignup} className="w-full mb-4">
            <img src={GoogleLogo} alt="Sign up with Google" className="" />
          </button>
        </div> */}
      </div>

      {/* Right Column */}
      <div className="w-full lg:w-1/2 bg-white flex justify-center items-center px-8 py-8 lg:px-12 lg:py-12 lg:pt-24">
        <div className="text-center">
          <h1 className="text-[#183053] text-2xl lg:text-3xl font-bold mb-4">
            {t("auth.freeTrialTitle")}
          </h1>
          <p className="text-[#183053] mb-6">
            {t("auth.freeTrialSubtitle")}{" "}
            <a
              className="text-blue-500 hover:text-blue-700"
              href="https://conferencecaptioning.com/app"
            >
              {t("auth.appStore")}
            </a>{" "}
            {t("auth.withoutAccount")}
          </p>
          {/* <img src={image} alt="Main Illustration" className=" w-[1000px] h-[600px]" style={{ maxWidth: '500px' }} />    */}
          <MyLottieAnimation />
        </div>
      </div>
    </div>
  );
};

export default SignUpPage;
